import React, { useState, useMemo } from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    Grid,
    Card,
    CardContent,
    Typography,
    Box,
    IconButton,
    Tooltip,
    InputAdornment,
    Chip,
    Divider,
    styled,
    useTheme,
} from '@mui/material'
import { SearchIcon, CloseIcon } from 'packages/eid-icons'
import * as Icons from 'packages/eid-icons'
import { Icon } from 'packages/eid-icons'

const StyledCard = styled(Card)(({ theme }) => ({
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    height: '140px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows[4],
        backgroundColor: theme.palette.action.hover,
    },
}))

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        maxWidth: '90vw',
        maxHeight: '90vh',
        width: '1200px',
        height: '800px',
    },
}))

// Icons that have fill: 'none' in their default props
const iconsWithFillNone = [
    'DoubleArrowSmallIcon',
    'ArrowScrollIcon',
    'ArrowRoundedBordersIcon',
    'ArrowCurvedRoundedBordersIcon',
    'ArrowSmallIcon',
]

const IconBrowser = ({ open, onClose }) => {
    const [searchTerm, setSearchTerm] = useState('')
    const [copiedIcon, setCopiedIcon] = useState(null)
    const theme = useTheme()

    // Extract all icon names from the Icons object
    const allIcons = useMemo(() => {
        const iconNames = Object.keys(Icons)
            .filter(
                (key) =>
                    key.endsWith('Icon') &&
                    key !== 'Icon' &&
                    key !== 'CheckIfIconExists',
            )
            .sort()

        return iconNames
    }, [])

    // Filter icons based on search term
    const filteredIcons = useMemo(() => {
        if (!searchTerm) return allIcons
        return allIcons.filter((iconName) =>
            iconName.toLowerCase().includes(searchTerm.toLowerCase()),
        )
    }, [allIcons, searchTerm])

    const handleCopyIconName = async (iconName) => {
        try {
            await navigator.clipboard.writeText(iconName.replace('Icon', ''))
            setCopiedIcon(iconName)
            setTimeout(() => setCopiedIcon(null), 2000)
        } catch (err) {
            console.error('Failed to copy icon name:', err)
        }
    }

    const handleCopyIconUsage = async (iconName) => {
        const usage = `<Icon name="${iconName.replace('Icon', '')}" />`
        try {
            await navigator.clipboard.writeText(usage)
            setCopiedIcon(`${iconName}-usage`)
            setTimeout(() => setCopiedIcon(null), 2000)
        } catch (err) {
            console.error('Failed to copy icon usage:', err)
        }
    }

    const IconCard = ({ iconName }) => {
        const isCopied = copiedIcon === iconName
        const isUsageCopied = copiedIcon === `${iconName}-usage`
        const nameWithoutIcon = iconName.replace('Icon', '')
        const hasFillNone = iconsWithFillNone.includes(iconName)

        return (
            <StyledCard
                onClick={() => handleCopyIconUsage(iconName)}
                sx={{
                    backgroundColor: isUsageCopied
                        ? theme.palette.success.light
                        : 'inherit',
                }}
            >
                <CardContent
                    sx={{
                        p: 1,
                        '&:last-child': { pb: 1 },
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        textAlign: 'center',
                    }}
                >
                    <Tooltip
                        title={
                            <Box>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 'bold' }}
                                >
                                    Click to copy icon name: "{nameWithoutIcon}"
                                </Typography>
                                {hasFillNone && (
                                    <Typography
                                        variant="caption"
                                        display="block"
                                        sx={{ color: 'warning.main', mt: 0.5 }}
                                    >
                                        Note: This icon has fill: none
                                    </Typography>
                                )}
                            </Box>
                        }
                        placement="top"
                    >
                        <Box
                            onClick={(e) => {
                                e.stopPropagation()
                                handleCopyIconName(iconName)
                            }}
                            sx={{
                                mb: 1,
                                p: 1,
                                borderRadius: '4px',
                                backgroundColor: isCopied
                                    ? theme.palette.success.light
                                    : '#f5f5f5',
                                border: '1px solid #e0e0e0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '60px',
                                height: '60px',
                                overflow: 'hidden',
                                cursor: 'pointer',
                                '&:hover': {
                                    backgroundColor: theme.palette.action.hover,
                                },
                                '& svg': {
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    width: 'auto',
                                    height: 'auto',
                                },
                            }}
                        >
                            <Icon
                                name={nameWithoutIcon}
                                style={{
                                    width: '40px',
                                    height: '40px',
                                    color: theme.palette.text.primary,
                                }}
                                fill={hasFillNone ? '#000000' : undefined}
                            />
                        </Box>
                    </Tooltip>

                    <Box sx={{ width: '100%' }}>
                        <Typography
                            variant="caption"
                            align="center"
                            sx={{
                                fontSize: '0.7rem',
                                lineHeight: 1.2,
                                wordBreak: 'break-word',
                                maxWidth: '100%',
                                display: 'block',
                                mb: hasFillNone ? 0.5 : 0,
                            }}
                        >
                            {nameWithoutIcon}
                        </Typography>

                        {hasFillNone && (
                            <Tooltip
                                title="This icon has fill: 'none' in its default props, which means it won't be filled with color by default. A temporary black fill is applied in this gallery for visibility."
                                placement="top"
                            >
                                <Chip
                                    label="fill: none"
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    sx={{
                                        fontSize: '0.6rem',
                                        height: '16px',
                                        mb: 0.5,
                                        cursor: 'help',
                                    }}
                                />
                            </Tooltip>
                        )}

                        {isCopied && (
                            <Tooltip title="Icon name copied to clipboard!">
                                <Chip
                                    label="Copied!"
                                    size="small"
                                    color="success"
                                    sx={{
                                        fontSize: '0.6rem',
                                        height: '16px',
                                    }}
                                />
                            </Tooltip>
                        )}

                        {isUsageCopied && (
                            <Tooltip title="Icon usage copied to clipboard!">
                                <Chip
                                    label="Usage Copied!"
                                    size="small"
                                    color="success"
                                    sx={{
                                        fontSize: '0.6rem',
                                        height: '16px',
                                    }}
                                />
                            </Tooltip>
                        )}
                    </Box>
                </CardContent>
            </StyledCard>
        )
    }

    return (
        <StyledDialog open={open} onClose={onClose} maxWidth={false}>
            <DialogTitle
                sx={{
                    pb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="h5" component="div">
                        Icon Browser
                    </Typography>
                    <Chip
                        label={`${filteredIcons.length} icons`}
                        size="small"
                        color="primary"
                        variant="outlined"
                    />
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ p: 3 }}>
                <Box mb={3}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Search icons... (e.g., 'user', 'arrow', 'check')"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ mb: 2 }}
                    />

                    <Typography variant="body2" color="textSecondary">
                        Click on an icon to copy its name, or click the card to
                        copy the usage syntax.
                    </Typography>
                </Box>

                <Grid
                    container
                    spacing={2}
                    sx={{ maxHeight: '500px', overflow: 'auto' }}
                >
                    {filteredIcons.map((iconName) => (
                        <Grid item xs={6} sm={4} md={3} lg={2} key={iconName}>
                            <Tooltip
                                title={
                                    <Box>
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 'bold' }}
                                        >
                                            {iconName.replace('Icon', '')}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            display="block"
                                        >
                                            Click icon: Copy name
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            display="block"
                                        >
                                            Click card: Copy usage
                                        </Typography>
                                        {iconsWithFillNone.includes(
                                            iconName,
                                        ) && (
                                            <Typography
                                                variant="caption"
                                                display="block"
                                                sx={{ color: 'warning.main' }}
                                            >
                                                Note: This icon has fill: none
                                            </Typography>
                                        )}
                                    </Box>
                                }
                                placement="top"
                            >
                                <IconCard iconName={iconName} />
                            </Tooltip>
                        </Grid>
                    ))}
                </Grid>

                {filteredIcons.length === 0 && (
                    <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        py={8}
                    >
                        <Typography
                            variant="h6"
                            color="textSecondary"
                            gutterBottom
                        >
                            No icons found
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Try a different search term
                        </Typography>
                    </Box>
                )}
            </DialogContent>
        </StyledDialog>
    )
}

export default IconBrowser
